import React, { useMemo } from 'react';
import { Button } from 'antd';
import moment from 'moment';

import {
  identity,
  isEitherAdmin,
  isStartup,
  isAngelInvestor,
  parseNumberToPrice,
} from 'utils';
import { RequestRowProps, StatusType } from 'types/requests';
import { ResponseRequestType as AllRequestType } from 'packages/service_request_repository';
import { ventureBuilderRequestSender } from 'packages/service_request_repository/ventureBuilder';
import { RenderIf } from 'components/components';
import RequestRowContent from './Content';
import { STATUS_COLOR_CLASS } from '../RequestDetailsDrawer';
import { isRejected, isUnsent } from '../../utils';
import { formatSlashMDYHD } from '../../../utils/constants/formatters';
import RequestRowButtons from '../RequestRowButtons';

import './RequestRow.scss';

const RequestRow = (props: RequestRowProps) => {
  const { request, highlight, userType } = props;

  const isServiceRequest = useMemo(() => (
    identity.isObjWithChildren((request as AllRequestType).serviceRequest)
  ), [request]);

  const { id, startup, angelInvestor } = request;

  const requestData = useMemo(() => {
    let requestTitle = '';
    let rowDataLeft;
    let rowDataRight = '';
    let requestStatus: StatusType | 'all' = 'all';
    let date = '';
    let logo = '';
    let providerLogo = '';
    let requestId = 0;
    const dataType: 'service' | 'partner' = isServiceRequest ? 'service' : 'partner';

    if (identity.isObjWithChildren(request)) {
      logo = request?.startup?.name !== '' ? startup?.imageLink as string : angelInvestor?.imageLink as string;

      if (isServiceRequest) {
        const {
          itemCount, itemList, totalPrice, status, applicationDate, serviceProvider, id: sId,
        } = (request as AllRequestType).serviceRequest!;
        requestTitle = (itemCount <= 1 && identity.isPositiveNumber(itemCount)) ? itemList[0] : 'Request for Multiple services';
        rowDataRight = `${parseNumberToPrice(totalPrice, 'AED')}`;
        requestStatus = status as StatusType;
        providerLogo = serviceProvider?.imageLink || '';
        requestId = sId;
        rowDataLeft = `From ${ventureBuilderRequestSender((request as AllRequestType).serviceRequest) || startup.name}`;
        date = moment(applicationDate).format(formatSlashMDYHD);
      } else {
        const {
          subject, createdBy, status, applicationDate, partner, id: pId,
        } = (request as AllRequestType).partnerRequest!;
        const { firstName, lastName } = createdBy;
        requestTitle = subject;
        date = moment(applicationDate).format(formatSlashMDYHD);
        rowDataRight = startup.name;
        requestStatus = status as StatusType;
        providerLogo = partner.logo as string || '';
        requestId = pId;
        rowDataLeft = `From ${identity.isTruthyString(firstName) || identity.isTruthyString(lastName) ? `${firstName} ${lastName}` : ''}`;
      }
    }
    return ({
      requestTitle,
      date,
      requestId,
      requestStatus,
      rowDataLeft,
      rowDataRight,
      dataType,
      logo,
      providerLogo,
    });
  }, [request]);

  if (!identity.isObjWithChildren(request)) {
    return null;
  }

  const {
    date,
    requestId,
    requestStatus,
    rowDataLeft,
    rowDataRight,
    dataType,
    requestTitle,
    logo,
    providerLogo,
  } = requestData;

  return (
    <RenderIf condition={identity.isTruthyNumber(id)}>
      <RequestRowContent
        requestId={requestId}
        requestTitle={requestTitle}
        requestStatus={requestStatus}
        rowDataLeft={rowDataLeft}
        rowDataRight={rowDataRight}
        date={date}
        highlight={highlight}
        unsent={isUnsent(requestStatus) && isServiceRequest}
        statusClassName={STATUS_COLOR_CLASS(userType)[requestStatus]}
        largeLogos={!isEitherAdmin(userType)}
        logos={[
          ...(isAngelInvestor(userType) || isStartup(userType) || isEitherAdmin(userType)
            ? [{ key: 'provider', src: providerLogo }] : []),
          ...(!isStartup(userType) && !isAngelInvestor(userType)
            ? [{ key: 'requester', src: logo }] : []),
        ]}
        statusAction={isStartup(userType!) && isRejected(requestStatus) && isServiceRequest
          ? <Button className="request-row__denied-see-why">See Why?</Button> : undefined}
        actions={<RequestRowButtons type={dataType} status={requestStatus} id={requestId} />}
      />
    </RenderIf>
  );
}

RequestRow.defaultProps = {
  highlight: false,
  requestType: 'service',
};

export default RequestRow;
